import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from "@mui/icons-material/Save";
import { Box, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useTranslation } from "react-i18next";
import { Resource } from "../../types/model/app/PrivateSettings";

interface TreeLabelProps {
  label: string;
  itemId: string;
  checked: boolean;
  onToggle: (key: string, isParent: boolean) => void;
  isParent: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

function TreeLabel({ label, checked, onToggle, itemId, isParent, disabled, hidden }: Readonly<TreeLabelProps>) {
  return (
    <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
      {!hidden && (
        <Checkbox
          checked={checked}
          onChange={() => onToggle(itemId, isParent)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          disabled={disabled}
        />
      )}
      {label}
    </Box>
  );
}

interface PermissionsFormProps {
  resources: Resource[];
  loading: boolean;
  checkedKeys: Set<string>;
  onToggle: (key: string, isParent: boolean) => void;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  showHeader?: boolean;
}

export default function PermissionsForm({
  resources,
  loading,
  checkedKeys,
  onToggle,
  isEditing,
  isSaving,
  onEdit,
  onSave,
  onCancel,
  showHeader = true,
}: Readonly<PermissionsFormProps>) {
  const { t } = useTranslation();
  const isModule = (key: string) => key.startsWith("MODULE_");

  const topLevel = resources.filter((r) => !r.parentKey && r.active);
  const childrenOf = (parentKey: string) =>
    resources.filter((r) => r.parentKey === parentKey && r.active);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
            {t("admin.accounts.permissions.title")}
          </Typography>

          {showHeader && (
            isEditing ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title={t("generic.save")}>
                  <IconButton color="success" onClick={onSave} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("generic.cancel")}>
                  <IconButton color="error" onClick={onCancel}>
                    <EditOffIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip title={t("generic.edit")}>
                <IconButton color="primary" onClick={onEdit}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>

        <SimpleTreeView>
          {topLevel.map((parent) => {
            const children = childrenOf(parent.key);
            return (
              <TreeItem
                key={parent.key}
                itemId={parent.key}
                label={
                  <TreeLabel
                    itemId={parent.key}
                    label={t(parent.i18nLabel, parent.key)}
                    checked={checkedKeys.has(parent.key)}
                    onToggle={isEditing ? onToggle : () => {}}
                    isParent={true}
                    disabled={!isEditing}
                    hidden={isModule(parent.key)}
                  />
                }
              >
                {children.map((child) => (
                  <TreeItem
                    key={child.key}
                    itemId={child.key}
                    label={
                      <TreeLabel
                        itemId={child.key}
                        label={t(child.i18nLabel, child.key)}
                        checked={checkedKeys.has(child.key)}
                        onToggle={isEditing ? onToggle : () => {}}
                        isParent={false}
                        disabled={!isEditing}
                      />
                    }
                  />
                ))}
              </TreeItem>
            );
          })}
        </SimpleTreeView>
      </Paper>
    </Box>
  );
}